import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLine, scrollToId } from "./motion";

export const Hero = ({
  overline,
  line1,
  line2Prefix,
  line2Highlight,
  subtitle,
  bgImage,
  primaryLabel = "Request a Quote",
  onPrimary,
  secondaryLabel = "Explore Products",
  onSecondary,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const archY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const archRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden bg-[#010101] flex items-end">
      <motion.div style={{ y: imgY }} className="absolute inset-0 z-0">
        <img src={bgImage} alt="" className="w-full h-[115%] object-cover opacity-40" style={{ objectPosition: "center 72%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#010101] via-[#010101]/70 to-[#010101]/30" />
      </motion.div>

      <motion.div style={{ y: archY, rotate: archRotate }} className="absolute -top-[20vh] right-[-10vw] z-0 w-[75vw] h-[75vw] max-w-[900px] max-h-[900px] pointer-events-none">
        <div className="sa-spin-slow w-full h-full rounded-full border-[3px] border-[#fcc41b]/70 sa-gold-glow" style={{ borderRightColor: "transparent", borderBottomColor: "transparent" }} />
        <div className="absolute inset-[12%] rounded-full bg-[#fcc41b]/[0.04] blur-3xl" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pb-24 pt-40">
        <MaskLine delay={0.35} className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-6">
          {overline}
        </MaskLine>

        <h1 className="font-display font-semibold tracking-tighter leading-[0.92] text-white" style={{ fontSize: "clamp(2.75rem, 8.5vw, 8.5rem)" }}>
          <MaskLine delay={0.45}>{line1}</MaskLine>
          <MaskLine delay={0.58}>
            {line2Prefix} <span className="text-[#fcc41b]">{line2Highlight}</span>
          </MaskLine>
        </h1>

        <div className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
            className="text-lg sm:text-xl text-zinc-300 max-w-xl leading-relaxed"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 1.05 }}
            className="flex flex-col sm:flex-row lg:flex-col gap-3"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={onPrimary || (() => scrollToId("quote"))}
              className="group inline-flex items-center justify-between gap-4 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-7 py-4 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300"
            >
              {primaryLabel}
              <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-secondary-btn"
              onClick={onSecondary || (() => scrollToId("products"))}
              className="group inline-flex items-center justify-between gap-4 rounded-full border border-[#fcc41b]/60 text-[#fcc41b] font-display font-medium px-7 py-4 hover:bg-[#fcc41b]/10 transition-colors duration-300"
            >
              {secondaryLabel}
              <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
